import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Header from 'components/Header/Header';
import Style from './TopicSelector.module.css';
import { logEventPagesViewed } from 'utils/analytics';
import { getMusicEngineHomeData } from 'api/api';

function TopicSelector() {
    const [selectedTopics, setSelectedTopics] = useState(new Set());
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const history = useHistory();
    const location = useLocation();

    const buildTopicsFromHome = (homeData) => {
        const channels = Array.isArray(homeData?.channels) ? homeData.channels : [];
        const channelTopics = channels
            .filter((ch) => {
                const title = (ch.title || '').toString().toLowerCase();
                const alt = (ch.alt_name || ch.slug || '').toString().toLowerCase();
                // Hide the Amplifying channel from selection
                return !(
                    /amplifying/.test(title) ||
                    /podcasthers/.test(title) ||
                    /her voice/.test(title) ||
                    /amplifying-her-voice/.test(alt)
                );
            })
            .map((ch) => ({
                id: (ch.alt_name || ch.slug || (ch.title || '').toLowerCase().replace(/\s+/g, '-')).toString(),
                name: ch.title || ch.name || 'Channel',
                description: ch.description || ''
            }));
        return channelTopics;
    };

    useEffect(() => {
        // Track page view (non-blocking)
        logEventPagesViewed(null, window.location.href, 'topic_selector_page', 'TopicSelector').catch(() => {});
        
        // Load topics from MusicEngine homepage
        (async () => {
            try {
                const home = await getMusicEngineHomeData();
                const builtTopics = buildTopicsFromHome(home);
                setTopics(builtTopics);
            } catch (err) {
                setError('Failed to load topics');
                // Fallback to only All option
                setTopics([{ id: 'all', name: 'All Topics', description: 'Show all content' }]);
            } finally {
                setLoading(false);
                loadSelectedTopics();
            }
        })();
        
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        if (location.state && location.state.selectedTopics) {
            loadSelectedTopics();
        }
    }, [location.state]);

    const loadSelectedTopics = () => {
        const locationSelectedTopics = location.state && location.state.selectedTopics;
        const storedTopics = localStorage.getItem('selectedTopics');
        
        let topicsSel = []; // Default to none (only Amplifying will show on Discover)
        
        if (locationSelectedTopics) {
            topicsSel = locationSelectedTopics;
        } else if (storedTopics) {
            try {
                topicsSel = JSON.parse(storedTopics);
            } catch (error) {
                topicsSel = [];
            }
        }

        // Remove any Amplifying-related selections from legacy storage
        const isAmplifying = (val) => /amplifying|podcasthers|her-voice/i.test(String(val || ''));
        if (Array.isArray(topicsSel)) {
            topicsSel = topicsSel.filter((id) => !isAmplifying(id));
        }

        // Enforce max 5 topics
        if (Array.isArray(topicsSel) && topicsSel.length > 5) {
            topicsSel = topicsSel.slice(0, 5);
        }

        setSelectedTopics(new Set(topicsSel));
    };

    const handleTopicClick = (topicId) => {
        const newSelectedTopics = new Set(selectedTopics);
        if (newSelectedTopics.has(topicId)) {
            newSelectedTopics.delete(topicId);
        } else {
            // Enforce max of 5 selectable topics
            if (newSelectedTopics.size >= 5) {
                return;
            }
            newSelectedTopics.add(topicId);
        }

        setSelectedTopics(newSelectedTopics);
    };

    const handleSaveTopics = () => {
        const selectedTopicsArray = Array.from(selectedTopics);
        
        // Store selected topics in localStorage
        localStorage.setItem('selectedTopics', JSON.stringify(selectedTopicsArray));
        
        // Navigate back to discover page with selected topics
        history.push('/podcast', { 
            selectedTopics: selectedTopicsArray 
        });
    };

    const handleBackToDiscover = () => {
        history.push('/podcast');
    };

    return (
        <div className={Style.container}>
            <Header 
                title="Select Topics" 
                back={true} 
                backHandler={handleBackToDiscover}
            />
            
            <div className={Style.content}>
                <div className={Style.header}>
                    <h2>Select Topics</h2>
                    <p>Choose the topics you want to see on the Discover page</p>
                    <p style={{ fontSize: '0.9em', marginTop: '4px' }}>Maximum 5 topics can be selected.</p>
                </div>

                {error && (
                    <div style={{ color: 'red', marginBottom: '8px' }}>{error}</div>
                )}

                <div className={Style.topicsGrid}>
                    {!loading && topics.map((topic) => (
                        <div
                            key={topic.id}
                            className={`${Style.topicCard} ${selectedTopics.has(topic.id) ? Style.selected : ''}`}
                            onClick={() => handleTopicClick(topic.id)}
                        >
                            <div className={Style.topicContent}>
                                <div className={Style.topicName}>{topic.name}</div>
                                <div className={Style.topicDescription}>{topic.description}</div>
                            </div>

                        </div>
                    ))}
                </div>

                <div className={Style.actions}>
                    <button 
                        className={Style.saveButton}
                        onClick={handleSaveTopics}
                    >
                        Save & Return to Discover
                    </button>
                </div>
            </div>
        </div>
    );
}

export default TopicSelector;

